import { useEffect, useState } from 'react';
import { MessageSquare, UserCheck, Timer, Loader2, Check, Radio, AlertCircle } from 'lucide-react';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { Interruptor } from '../../../components/Interruptor';
import { useAuth } from '../../../lib/auth';
import { atendimentoApi } from '../lib/api';
import { ModalContatos } from './ModalContatos';
import { ModalPerfilInbox } from './ModalPerfilInbox';

interface Props {
  aberto: boolean;
  onFechar: () => void;
}

type Aba = 'contatos' | 'perfil' | 'redistribuicao' | 'canal';

const ABAS: Array<{ id: Aba; label: string; icon: typeof MessageSquare }> = [
  { id: 'contatos', label: 'Contatos', icon: MessageSquare },
  { id: 'perfil', label: 'Perfil', icon: UserCheck },
  { id: 'redistribuicao', label: 'Redistribuicao', icon: Timer },
  { id: 'canal', label: 'Canal e automaticos', icon: Radio },
];

const noop = () => {};

export function ModalChatAtendimento({ aberto, onFechar }: Props) {
  const [aba, setAba] = useState<Aba>('contatos');

  // Reseta pra primeira aba toda vez que abre.
  useEffect(() => { if (aberto) setAba('contatos'); }, [aberto]);

  return (
    <Modal aberto={aberto} onFechar={onFechar} titulo="Chat Atendimento" largura={860}>
      {/* Barra de abas */}
      <div className="flex items-center gap-1 mb-4 border-b" style={{ borderColor: 'var(--panel-border, rgba(0,0,0,0.12))' }}>
        {ABAS.map((a) => {
          const ativa = aba === a.id;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setAba(a.id)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium"
              style={{
                color: ativa ? 'var(--primary)' : 'inherit',
                opacity: ativa ? 1 : 0.65,
                borderBottom: ativa ? '2px solid var(--primary)' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              <a.icon size={15} />
              {a.label}
            </button>
          );
        })}
      </div>

      {/* Conteudo da aba ativa (monta sob demanda — carrega os dados so ao abrir a aba) */}
      <div style={{ minHeight: 360 }}>
        {aba === 'contatos' && <ModalContatos aberto onFechar={noop} embutido />}
        {aba === 'perfil' && <ModalPerfilInbox aberto onFechar={noop} embutido />}
        {aba === 'redistribuicao' && (
          <div className="space-y-6">
            <AbaRedistribuicao />
            <div className="border-t pt-5" style={{ borderColor: 'var(--panel-border, rgba(0,0,0,0.12))' }}>
              <BlocoJanelaCongelar />
            </div>
          </div>
        )}
        {aba === 'canal' && <AbaCanal />}
      </div>
    </Modal>
  );
}

const CHAVE_CANAL = 'canal_atendimento';
const CHAVE_PROMO = 'promo_automatica';

// Canal do Atendimento (RDS chamados 1.24) + disparo automatico de promocao (1.25).
//
// Os dois estados nascem `null` = "ainda nao sei", e os interruptores so aparecem quando
// o valor chega do banco (DA-463). `useState(true)` aqui seria uma AFIRMACAO: a chave
// desenharia "nuvem da Meta" sem ter lido nada, e num campo que decide por onde a loja
// inteira fala isso e' pior que a tela demorar meio segundo.
function AbaCanal() {
  const toast = useToast();
  const { usuario } = useAuth();
  const ehAdmin = usuario?.perfil === 'admin';
  const [baileys, setBaileys] = useState<boolean | null>(null);
  const [promo, setPromo] = useState<boolean | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    Promise.all([
      atendimentoApi.obterSetting(CHAVE_CANAL).catch(() => ({ valor: null })),
      atendimentoApi.obterSetting(CHAVE_PROMO).catch(() => ({ valor: null })),
    ]).then(([c, p]) => {
      if (cancelado) return;
      setBaileys(c.valor === 'baileys');
      setPromo(p.valor !== 'false');
    });
    return () => { cancelado = true; };
  }, []);

  async function trocarCanal(usarBaileys: boolean) {
    setSalvando(CHAVE_CANAL);
    const anterior = baileys;
    setBaileys(usarBaileys);
    try {
      await atendimentoApi.atualizarSetting(CHAVE_CANAL, usarBaileys ? 'baileys' : 'cloud');
      toast.sucesso(usarBaileys
        ? 'Atendimento passou a sair pelo aparelho da loja.'
        : 'Atendimento voltou para a nuvem da Meta.');
    } catch (e: any) {
      setBaileys(anterior);
      toast.erro(e?.message ?? 'Falha ao trocar o canal');
    } finally {
      setSalvando(null);
    }
  }

  async function trocarPromo(ligado: boolean) {
    setSalvando(CHAVE_PROMO);
    const anterior = promo;
    setPromo(ligado);
    try {
      await atendimentoApi.atualizarSetting(CHAVE_PROMO, ligado ? 'true' : 'false');
      toast.sucesso(ligado ? 'Disparo automatico religado.' : 'Atendimento agora e 100% reativo.');
    } catch (e: any) {
      setPromo(anterior);
      toast.erro(e?.message ?? 'Falha ao salvar');
    } finally {
      setSalvando(null);
    }
  }

  if (baileys === null || promo === null) {
    return <div className="flex items-center justify-center py-8 opacity-70"><Loader2 size={18} className="animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <label className="text-sm font-medium block mb-2">Por onde o Atendimento entra e sai</label>
        <Interruptor
          ligado={baileys}
          onChange={trocarCanal}
          desabilitado={!ehAdmin || salvando === CHAVE_CANAL}
          rotuloLigado="Aparelho da loja"
          rotuloDesligado="Nuvem da Meta (API oficial)"
          ajuda={baileys
            ? 'Sem trava de 24h, da pra editar mensagem e apagar para todos. "Enviar produto" vai como foto com o link embaixo.'
            : 'Trava de 24h ativa (bolinha azul/cinza na lista). Nao da pra editar nem apagar para todos.'}
        />
        <div className="flex gap-2 items-start p-3 rounded-lg mt-3" style={{ backgroundColor: '#f97316', border: '1px solid #c2410c' }}>
          <AlertCircle size={18} style={{ color: '#000' }} className="flex-shrink-0 mt-0.5" />
          <div className="text-xs leading-relaxed" style={{ color: '#000' }}>
            <b>Trocar aqui nao move o numero.</b> O WhatsApp so aceita o numero num lugar por vez.
            Aponte para o aparelho <b>depois</b> de registrar o numero no WhatsApp Business do celular
            da loja e parear em "Conexao Baileys (Atendimento)". Na ordem errada, o Atendimento fica mudo.
          </div>
        </div>
        {!ehAdmin && (
          <p className="text-xs opacity-65 mt-2">Somente o administrador troca o canal.</p>
        )}
      </div>

      <div className="border-t pt-5" style={{ borderColor: 'var(--panel-border, rgba(0,0,0,0.12))' }}>
        <label className="text-sm font-medium block mb-2">Disparo automatico de promocao</label>
        <Interruptor
          ligado={promo}
          onChange={trocarPromo}
          desabilitado={salvando === CHAVE_PROMO}
          rotuloLigado="Ligado"
          rotuloDesligado="Desligado (Atendimento 100% reativo)"
          ajuda={promo
            ? 'Quem escreve recebe o fly do dia (1x por dia por numero); quem escreve "todas" recebe o link do catalogo.'
            : 'A loja so responde quem escreveu. Quem mandar "todas" vira chamado normal e e atendido por uma pessoa.'}
        />
        <p className="text-xs opacity-65 mt-3 leading-relaxed">
          Nao apaga promocao nenhuma e nao tira o envio manual — vale so para o que o sistema
          dispara sozinho. Pelo aparelho da loja, mensagem enviada sem ninguem ter pedido e o que
          mais pesa contra o numero, entao desligar aqui e a forma de proteger o numero sem abrir
          mao da gestao de promocoes.
        </p>
      </div>
    </div>
  );
}

const CHAVE_GOTEJO = 'fila_espera_gotejo_min';
const DEFAULT_MIN = 2;

function AbaRedistribuicao() {
  const toast = useToast();
  const [valor, setValor] = useState<string>(String(DEFAULT_MIN));
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    atendimentoApi.obterSetting(CHAVE_GOTEJO)
      .then((r) => { if (!cancelado) setValor(r.valor ?? String(DEFAULT_MIN)); })
      .catch(() => {})
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, []);

  async function salvar() {
    const min = Number(valor);
    if (!Number.isFinite(min) || min < 1 || min > 60) {
      toast.erro('Informe um numero de minutos entre 1 e 60.');
      return;
    }
    setSalvando(true);
    try {
      await atendimentoApi.atualizarSetting(CHAVE_GOTEJO, String(Math.round(min)));
      toast.sucesso(`Tempo de redistribuicao atualizado para ${Math.round(min)} min.`);
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao salvar');
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-8 opacity-70">
        <Loader2 size={18} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-md">
      <label className="text-sm font-medium block mb-1">
        Tempo para redistribuicao de chamados orfaos
      </label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={1}
          max={60}
          step={1}
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          className="w-24 px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
          style={{ borderColor: 'var(--input-border, rgba(0,0,0,0.15))', fontSize: '16px' }}
        />
        <span className="text-sm opacity-70">minutos</span>
        <button
          type="button"
          className="btn-primario flex items-center gap-1.5 ml-2"
          onClick={salvar}
          disabled={salvando}
        >
          {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {salvando ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
      <p className="text-xs opacity-65 mt-3 leading-relaxed">
        Quando uma conversa fica sem dono (cliente esperando e ninguem pegou), o sistema
        distribui essas conversas <b>aos poucos</b> para a equipe em turno: solta no maximo
        um chamado por atendente a cada intervalo definido aqui — o mais antigo na espera
        primeiro. O primeiro ja sai na hora em que a atendente bate ponto. Vale so para o
        acumulo represado (fim de semana, madrugada); mensagem nova ao vivo continua sendo
        atribuida na hora. A mudanca passa a valer imediatamente, sem reiniciar o sistema.
      </p>
    </div>
  );
}

const CHAVE_JANELA = 'chamado_congelar_janela_min';
const DEFAULT_JANELA = 30;

// Janela em que da pra "Congelar e sair" ao bater saida com chamado em aberto (RDS 1.17/1.18).
function BlocoJanelaCongelar() {
  const toast = useToast();
  const [valor, setValor] = useState<string>(String(DEFAULT_JANELA));
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    atendimentoApi.obterSetting(CHAVE_JANELA)
      .then((r) => { if (!cancelado) setValor(r.valor ?? String(DEFAULT_JANELA)); })
      .catch(() => {})
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, []);

  async function salvar() {
    const min = Number(valor);
    if (!Number.isFinite(min) || min < 5 || min > 120) {
      toast.erro('Informe um numero de minutos entre 5 e 120.');
      return;
    }
    setSalvando(true);
    try {
      await atendimentoApi.atualizarSetting(CHAVE_JANELA, String(Math.round(min)));
      toast.sucesso(`Janela para congelar atualizada para ${Math.round(min)} min.`);
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao salvar');
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-6 opacity-70">
        <Loader2 size={18} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-md">
      <label className="text-sm font-medium block mb-1">
        Janela para congelar chamado ao sair
      </label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={5}
          max={120}
          step={5}
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          className="w-24 px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
          style={{ borderColor: 'var(--input-border, rgba(0,0,0,0.15))', fontSize: '16px' }}
        />
        <span className="text-sm opacity-70">minutos antes da saida</span>
        <button
          type="button"
          className="btn-primario flex items-center gap-1.5 ml-2"
          onClick={salvar}
          disabled={salvando}
        >
          {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {salvando ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
      <p className="text-xs opacity-65 mt-3 leading-relaxed">
        Ao bater a <b>saida final do dia</b> com chamados ainda em aberto, o sistema barra a saida e
        pede para resolver, transferir ou <b>congelar</b> (deixar para amanha). "Saida final" e esta
        janela — destes minutos antes do horario de saida cadastrado em diante. Saidas mais cedo
        (almoco, intervalo) e dias de folga passam livres, sem barrar. Vale imediatamente, sem reiniciar.
      </p>
    </div>
  );
}
